import type { PortalIdentityResolverResult } from './portalIdentityResolver';

export type PortalIdentityEnforcementDecision =
  | 'allow'
  | 'warn_only'
  | 'would_block_later'
  | 'manual_review'
  | 'never_auto_block'
  | 'not_applicable';

export type PortalIdentityEnforcementSeverity = 'info' | 'warning' | 'critical';

export type PortalIdentityEnforcementPreview = {
  decision: PortalIdentityEnforcementDecision;
  reason: string;
  safeLabel: string;
  canEnforceLater: boolean;
  severity: PortalIdentityEnforcementSeverity;
  internalNote: string;
};

export type PortalIdentityRouteEnforcementDryRun = {
  mode: 'dry_run';
  enforcing: false;
  decision: PortalIdentityEnforcementDecision;
  reason: string;
  safeLabel: string;
  severity: PortalIdentityEnforcementSeverity;
  wouldAllowNow: true;
  wouldBlockLater: boolean;
  internalNote: string;
};

export type LegacyRouteReadinessState =
  | 'legacy_allows'
  | 'legacy_redirects'
  | 'legacy_blocks'
  | 'legacy_loading'
  | 'legacy_unknown';

export type RouteEnforcementReadinessHarnessInput = {
  portal: 'hire' | 'seek' | 'admin';
  legacyRouteReadiness: LegacyRouteReadinessState;
  diagnostic: PortalIdentityResolverResult | null;
};

export type RouteEnforcementReadinessHarnessResult = {
  mode: 'readiness_harness';
  enforcing: false;
  portal: 'hire' | 'seek' | 'admin';
  legacyRouteReadiness: LegacyRouteReadinessState;
  actualRouteAuthority: 'legacy_guard';
  actualRouteBehaviorChanged: false;
  portalIdentityDecision: PortalIdentityEnforcementDecision;
  portalIdentityReason: string;
  portalIdentitySeverity: PortalIdentityEnforcementSeverity;
  dryRunWouldAllowNow: true;
  dryRunWouldBlockLater: boolean;
  safeLabel: string;
  internalNote: string;
};

export type RouteEnforcementWarningModeDecision =
  | 'no_warning'
  | 'future_warning_candidate'
  | 'manual_review_warning'
  | 'suppress_warning';

export type RouteEnforcementWarningModePlan = {
  mode: 'warning_mode_plan';
  enforcing: false;
  portal: 'hire' | 'seek' | 'admin';
  legacyRouteReadiness: LegacyRouteReadinessState;
  actualRouteAuthority: 'legacy_guard';
  actualRouteBehaviorChanged: false;
  portalIdentityDecision: PortalIdentityEnforcementDecision;
  portalIdentityReason: string;
  warningDecision: RouteEnforcementWarningModeDecision;
  warningSurface: 'none_currently';
  customerFacingUiChanged: false;
  wouldBlockNow: false;
  wouldRedirectNow: false;
  wouldSignOutNow: false;
  safeLabel: string;
  internalNote: string;
};

export type AdminEnforcementTrialEnvironment =
  | 'local'
  | 'development'
  | 'non_production'
  | 'staging'
  | 'production'
  | 'unknown';

export type AdminEnforcementTrialReadinessDecision =
  | 'candidate_after_explicit_approval'
  | 'blocked_production_or_staging'
  | 'blocked_out_of_scope_portal'
  | 'blocked_resolver_unavailable'
  | 'blocked_pending_identity_review'
  | 'blocked_pending_identity_inventory'
  | 'blocked_until_explicit_approval';

export type AdminEnforcementTrialReadinessInput = RouteEnforcementReadinessHarnessInput & {
  environment: AdminEnforcementTrialEnvironment;
};

export type AdminEnforcementTrialReadinessPlan = {
  mode: 'admin_non_production_enforcement_trial_plan';
  enforcing: false;
  portal: 'hire' | 'seek' | 'admin';
  environment: AdminEnforcementTrialEnvironment;
  legacyRouteReadiness: LegacyRouteReadinessState;
  actualRouteAuthority: 'legacy_guard';
  actualRouteBehaviorChanged: false;
  currentBehavior: 'legacy_guard_authoritative';
  futureTrialBehavior: 'blocked_until_explicit_approval';
  productionOrStagingEligible: false;
  forkHireForkSeekOutOfScope: boolean;
  adminOnlyTrial: boolean;
  portalIdentityDecision: PortalIdentityEnforcementDecision;
  portalIdentityReason: string;
  trialReadinessDecision: AdminEnforcementTrialReadinessDecision;
  explicitApprovalRequired: true;
  customerFacingUiChanged: false;
  wouldBlockNow: false;
  wouldRedirectNow: false;
  wouldSignOutNow: false;
  safeLabel: string;
  internalNote: string;
};

export type AdminEnforcementTrialWiringDecision =
  | 'default_legacy_authoritative'
  | 'evaluate_trial_readiness_only'
  | 'trial_block_candidate_after_approval'
  | 'blocked_production_or_staging'
  | 'blocked_until_explicit_approval';

export type AdminEnforcementTrialWiringInput = {
  trialFlagEnabled: boolean;
  environment: AdminEnforcementTrialEnvironment;
  legacyRouteReadiness: LegacyRouteReadinessState;
  diagnostic: PortalIdentityResolverResult | null;
};

export type AdminEnforcementTrialWiringPlan = {
  mode: 'admin_enforcement_trial_wiring_plan';
  enforcing: false;
  portal: 'admin';
  trialFlagEnabled: boolean;
  trialEvaluationActive: boolean;
  environment: AdminEnforcementTrialEnvironment;
  legacyRouteReadiness: LegacyRouteReadinessState;
  actualRouteAuthority: 'legacy_guard';
  actualRouteBehaviorChanged: false;
  currentBehavior: 'legacy_guard_authoritative';
  productionOrStagingEligible: false;
  forkHireForkSeekOutOfScope: false;
  adminOnlyTrial: true;
  trialWiringDecision: AdminEnforcementTrialWiringDecision;
  trialReadinessDecision: AdminEnforcementTrialReadinessDecision;
  portalIdentityDecision: PortalIdentityEnforcementDecision;
  portalIdentityReason: string;
  explicitApprovalRequired: true;
  customerFacingUiChanged: false;
  wouldBlockNow: false;
  wouldRedirectNow: false;
  wouldSignOutNow: false;
  wouldEnterRedirectLoop: false;
  safeLabel: string;
  internalNote: string;
};

const PORTAL_MISMATCH_REASONS = new Set([
  'invalid_portal',
  'portal_user_type_mismatch',
  'auth_source_mismatch',
  'ambiguous_identity',
]);

function preview(
  decision: PortalIdentityEnforcementDecision,
  reason: string,
  safeLabel: string,
  canEnforceLater: boolean,
  severity: PortalIdentityEnforcementSeverity,
  internalNote: string,
): PortalIdentityEnforcementPreview {
  return {
    decision,
    reason,
    safeLabel,
    canEnforceLater,
    severity,
    internalNote,
  };
}

export function classifyPortalIdentityForEnforcement(
  diagnostic: PortalIdentityResolverResult | null,
): PortalIdentityEnforcementPreview {
  if (!diagnostic) {
    return preview(
      'not_applicable',
      'not_loaded',
      'No portal identity result loaded',
      false,
      'info',
      'No enforcement action is available without a loaded diagnostic result.',
    );
  }

  if (diagnostic.reason === 'unsafe_response') {
    return preview(
      'never_auto_block',
      'unsafe_response',
      'Unsafe resolver response',
      false,
      'critical',
      'Investigate the resolver contract before considering enforcement.',
    );
  }

  if (diagnostic.reason === 'resolver_error') {
    return preview(
      'never_auto_block',
      'resolver_error',
      'Resolver unavailable',
      false,
      'warning',
      'Fail open for now; do not block users on resolver availability.',
    );
  }

  if (diagnostic.reason === 'unauthenticated') {
    return preview(
      'not_applicable',
      'unauthenticated',
      'No authenticated session',
      false,
      'info',
      'Existing auth routing handles unauthenticated sessions.',
    );
  }

  if (PORTAL_MISMATCH_REASONS.has(diagnostic.reason)) {
    return preview(
      'would_block_later',
      diagnostic.reason,
      'Portal identity mismatch',
      true,
      'critical',
      'Future enforcement may block this state after rollout gates are approved.',
    );
  }

  if (diagnostic.auth_identity_kind === 'google_direct_legacy') {
    return preview(
      'manual_review',
      'google_direct_legacy',
      'External-provider legacy identity',
      false,
      'warning',
      'External-provider identities remain non-authoritative until the final provider identity model is approved.',
    );
  }

  if (diagnostic.ready && diagnostic.reason === 'ok') {
    return preview(
      'allow',
      'ok',
      'Ready portal identity',
      true,
      'info',
      'This is the only state currently eligible for future enforcement after rollout approval.',
    );
  }

  if (diagnostic.reason === 'identity_not_found') {
    return preview(
      'would_block_later',
      'identity_not_found',
      'Portal identity missing',
      true,
      'warning',
      'Future enforcement may block after inventory and remediation are complete.',
    );
  }

  if (diagnostic.reason === 'identity_legacy_pending') {
    return preview(
      'warn_only',
      'identity_legacy_pending',
      'Legacy identity pending activation',
      true,
      'warning',
      'Warn only until activation readiness is complete for the target environment.',
    );
  }

  if (diagnostic.reason === 'identity_needs_review') {
    return preview(
      'manual_review',
      'identity_needs_review',
      'Identity needs review',
      false,
      'warning',
      'Manual review is required before this identity can become authoritative.',
    );
  }

  if (diagnostic.reason === 'identity_disabled') {
    return preview(
      'would_block_later',
      'identity_disabled',
      'Portal identity disabled',
      true,
      'critical',
      'Future enforcement should block disabled identities after rollout approval.',
    );
  }

  return preview(
    'warn_only',
    diagnostic.reason || 'unknown',
    'Unclassified portal identity state',
    false,
    'warning',
    'Keep diagnostic-only until this state has an approved enforcement policy.',
  );
}

export function previewPortalIdentityRouteEnforcementDryRun(
  previewResult: PortalIdentityEnforcementPreview,
): PortalIdentityRouteEnforcementDryRun {
  const wouldBlockLater = previewResult.decision === 'would_block_later';

  return {
    mode: 'dry_run',
    enforcing: false,
    decision: previewResult.decision,
    reason: previewResult.reason,
    safeLabel: wouldBlockLater
      ? 'Would block later after rollout approval'
      : 'Would allow now in dry-run',
    severity: previewResult.severity,
    wouldAllowNow: true,
    wouldBlockLater,
    internalNote: wouldBlockLater
      ? 'Dry-run only. Existing route checks still decide access.'
      : 'Dry-run only. No route access decision uses this result.',
  };
}

export function buildRouteEnforcementReadinessHarness(
  input: RouteEnforcementReadinessHarnessInput,
): RouteEnforcementReadinessHarnessResult {
  const portalIdentityPreview = classifyPortalIdentityForEnforcement(input.diagnostic);
  const dryRun = previewPortalIdentityRouteEnforcementDryRun(portalIdentityPreview);

  return {
    mode: 'readiness_harness',
    enforcing: false,
    portal: input.portal,
    legacyRouteReadiness: input.legacyRouteReadiness,
    actualRouteAuthority: 'legacy_guard',
    actualRouteBehaviorChanged: false,
    portalIdentityDecision: portalIdentityPreview.decision,
    portalIdentityReason: portalIdentityPreview.reason,
    portalIdentitySeverity: portalIdentityPreview.severity,
    dryRunWouldAllowNow: dryRun.wouldAllowNow,
    dryRunWouldBlockLater: dryRun.wouldBlockLater,
    safeLabel: 'Readiness harness only - current route behavior remains legacy-authoritative',
    internalNote:
      'Non-enforcing comparison only. Portal identity dry-run output must not control access until a later approved route enforcement slice.',
  };
}

function classifyWarningModeDecision(
  harness: RouteEnforcementReadinessHarnessResult,
): RouteEnforcementWarningModeDecision {
  if (harness.portalIdentityDecision === 'allow') {
    return 'no_warning';
  }

  if (
    harness.portalIdentityDecision === 'manual_review' ||
    harness.portalIdentityReason === 'identity_needs_review'
  ) {
    return 'manual_review_warning';
  }

  if (
    harness.portalIdentityDecision === 'never_auto_block' ||
    harness.portalIdentityDecision === 'not_applicable'
  ) {
    return 'suppress_warning';
  }

  return 'future_warning_candidate';
}

export function planRouteEnforcementWarningMode(
  input: RouteEnforcementReadinessHarnessInput,
): RouteEnforcementWarningModePlan {
  const harness = buildRouteEnforcementReadinessHarness(input);
  const warningDecision = classifyWarningModeDecision(harness);

  return {
    mode: 'warning_mode_plan',
    enforcing: false,
    portal: harness.portal,
    legacyRouteReadiness: harness.legacyRouteReadiness,
    actualRouteAuthority: 'legacy_guard',
    actualRouteBehaviorChanged: false,
    portalIdentityDecision: harness.portalIdentityDecision,
    portalIdentityReason: harness.portalIdentityReason,
    warningDecision,
    warningSurface: 'none_currently',
    customerFacingUiChanged: false,
    wouldBlockNow: false,
    wouldRedirectNow: false,
    wouldSignOutNow: false,
    safeLabel: 'Warning-mode plan only - no route or customer-facing behavior changes',
    internalNote:
      'Future warning-mode output is modeled for tests only. Current routes remain legacy-authoritative and no warning UI is rendered.',
  };
}

export function planForkHireRouteWarningModeShadow(
  input: Omit<RouteEnforcementReadinessHarnessInput, 'portal'>,
): RouteEnforcementWarningModePlan {
  return planRouteEnforcementWarningMode({
    portal: 'hire',
    legacyRouteReadiness: input.legacyRouteReadiness,
    diagnostic: input.diagnostic,
  });
}

export function planForkSeekRouteWarningModeShadow(
  input: Omit<RouteEnforcementReadinessHarnessInput, 'portal'>,
): RouteEnforcementWarningModePlan {
  return planRouteEnforcementWarningMode({
    portal: 'seek',
    legacyRouteReadiness: input.legacyRouteReadiness,
    diagnostic: input.diagnostic,
  });
}

function isProductionLikeEnvironment(environment: AdminEnforcementTrialEnvironment): boolean {
  return environment === 'production' || environment === 'staging';
}

function classifyAdminTrialReadiness(
  input: AdminEnforcementTrialReadinessInput,
  harness: RouteEnforcementReadinessHarnessResult,
): AdminEnforcementTrialReadinessDecision {
  if (input.portal !== 'admin') {
    return 'blocked_out_of_scope_portal';
  }

  if (isProductionLikeEnvironment(input.environment)) {
    return 'blocked_production_or_staging';
  }

  if (harness.portalIdentityDecision === 'never_auto_block') {
    return 'blocked_resolver_unavailable';
  }

  if (harness.portalIdentityDecision === 'manual_review') {
    return 'blocked_pending_identity_review';
  }

  if (
    harness.portalIdentityReason === 'identity_not_found' ||
    harness.portalIdentityReason === 'identity_legacy_pending'
  ) {
    return 'blocked_pending_identity_inventory';
  }

  if (
    harness.portalIdentityDecision === 'allow' ||
    harness.portalIdentityDecision === 'would_block_later'
  ) {
    return 'candidate_after_explicit_approval';
  }

  return 'blocked_until_explicit_approval';
}

export function planAdminNonProductionEnforcementTrialReadiness(
  input: AdminEnforcementTrialReadinessInput,
): AdminEnforcementTrialReadinessPlan {
  const harness = buildRouteEnforcementReadinessHarness({
    portal: input.portal,
    legacyRouteReadiness: input.legacyRouteReadiness,
    diagnostic: input.diagnostic,
  });
  const forkHireForkSeekOutOfScope = input.portal !== 'admin';
  const trialReadinessDecision = classifyAdminTrialReadiness(input, harness);

  return {
    mode: 'admin_non_production_enforcement_trial_plan',
    enforcing: false,
    portal: input.portal,
    environment: input.environment,
    legacyRouteReadiness: input.legacyRouteReadiness,
    actualRouteAuthority: 'legacy_guard',
    actualRouteBehaviorChanged: false,
    currentBehavior: 'legacy_guard_authoritative',
    futureTrialBehavior: 'blocked_until_explicit_approval',
    productionOrStagingEligible: false,
    forkHireForkSeekOutOfScope,
    adminOnlyTrial: !forkHireForkSeekOutOfScope,
    portalIdentityDecision: harness.portalIdentityDecision,
    portalIdentityReason: harness.portalIdentityReason,
    trialReadinessDecision,
    explicitApprovalRequired: true,
    customerFacingUiChanged: false,
    wouldBlockNow: false,
    wouldRedirectNow: false,
    wouldSignOutNow: false,
    safeLabel:
      'Admin non-production enforcement trial plan only - current route behavior remains legacy-authoritative',
    internalNote:
      'Planning/tests only. No route guard imports this helper yet; production/staging and ForkHire/ForkSeek enforcement trials remain blocked.',
  };
}

function classifyAdminTrialWiringDecision(
  trialFlagEnabled: boolean,
  plan: AdminEnforcementTrialReadinessPlan,
): AdminEnforcementTrialWiringDecision {
  if (!trialFlagEnabled) {
    return 'default_legacy_authoritative';
  }

  if (plan.trialReadinessDecision === 'blocked_production_or_staging') {
    return 'blocked_production_or_staging';
  }

  if (plan.portalIdentityDecision === 'allow') {
    return 'evaluate_trial_readiness_only';
  }

  if (
    plan.portalIdentityDecision === 'would_block_later' ||
    plan.portalIdentityDecision === 'manual_review' ||
    plan.portalIdentityReason === 'identity_legacy_pending' ||
    plan.portalIdentityReason === 'identity_not_found'
  ) {
    return 'trial_block_candidate_after_approval';
  }

  return 'blocked_until_explicit_approval';
}

export function planAdminEnforcementTrialWiring(
  input: AdminEnforcementTrialWiringInput,
): AdminEnforcementTrialWiringPlan {
  const readinessPlan = planAdminNonProductionEnforcementTrialReadiness({
    portal: 'admin',
    environment: input.environment,
    legacyRouteReadiness: input.legacyRouteReadiness,
    diagnostic: input.diagnostic,
  });
  const productionLike = isProductionLikeEnvironment(input.environment);
  const trialEvaluationActive = input.trialFlagEnabled && !productionLike;
  const trialWiringDecision = classifyAdminTrialWiringDecision(
    input.trialFlagEnabled,
    readinessPlan,
  );

  return {
    mode: 'admin_enforcement_trial_wiring_plan',
    enforcing: false,
    portal: 'admin',
    trialFlagEnabled: input.trialFlagEnabled,
    trialEvaluationActive,
    environment: input.environment,
    legacyRouteReadiness: input.legacyRouteReadiness,
    actualRouteAuthority: 'legacy_guard',
    actualRouteBehaviorChanged: false,
    currentBehavior: 'legacy_guard_authoritative',
    productionOrStagingEligible: false,
    forkHireForkSeekOutOfScope: false,
    adminOnlyTrial: true,
    trialWiringDecision,
    trialReadinessDecision: readinessPlan.trialReadinessDecision,
    portalIdentityDecision: readinessPlan.portalIdentityDecision,
    portalIdentityReason: readinessPlan.portalIdentityReason,
    explicitApprovalRequired: true,
    customerFacingUiChanged: false,
    wouldBlockNow: false,
    wouldRedirectNow: false,
    wouldSignOutNow: false,
    wouldEnterRedirectLoop: false,
    safeLabel:
      input.trialFlagEnabled && !productionLike
        ? 'Admin local enforcement trial wiring is evaluation-only'
        : 'Admin route remains legacy-authoritative; trial wiring is inactive',
    internalNote:
      'Disabled-by-default Admin-only wiring. This plan is safe to compute but must not control routing until a later explicitly approved local/non-production smoke step.',
  };
}
